'use client';

// Voice input for the chat composer — mic button (MediaRecorder, webm/opus) ->
// transcribe -> append text into the composer (never auto-send). Backend is
// POST /api/transcribe-audio (see apps/server/src/routes/transcribe-audio.ts):
// ElevenLabs Scribe when configured, WhisperX fallback, 501 when neither is
// available. The GET on that same route is a cheap capability check so the
// mic button can hide itself instead of always failing.
import { useCallback, useEffect, useRef, useState } from 'react';

/** Feature-detect only — no permission prompt, no server round trip. */
export function isVoiceRecordingSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

// Module-level singleton: both composer instances (home + in-conversation)
// mount at different times but share one capability fetch for the page's
// lifetime instead of hammering the endpoint twice.
let capabilityPromise: Promise<boolean> | null = null;

function fetchTranscriptionCapability(): Promise<boolean> {
  if (!capabilityPromise) {
    capabilityPromise = fetch('/api/transcribe-audio')
      .then((res) => (res.ok ? res.json() : { available: false }))
      .then((data: { available?: boolean }) => data.available === true)
      .catch(() => false);
  }
  return capabilityPromise;
}

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return undefined;
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type));
}

export type VoiceRecorderStatus = 'idle' | 'recording' | 'transcribing';

export interface UseVoiceRecorderOptions {
  /** Called with the transcript text — caller appends it into the composer. */
  onTranscript: (text: string) => void;
  /** Called with a user-facing error message (permission denied, transcription failure, ...). */
  onError: (message: string) => void;
}

export interface UseVoiceRecorderResult {
  /** Browser can record audio at all — false hides the mic button entirely. */
  supported: boolean;
  /** A transcription backend is configured server-side — false hides the mic button. */
  available: boolean;
  status: VoiceRecorderStatus;
  /** Click handler: idle -> start recording, recording -> stop + transcribe. */
  toggle: () => void;
}

export function useVoiceRecorder({ onTranscript, onError }: UseVoiceRecorderOptions): UseVoiceRecorderResult {
  const [status, setStatus] = useState<VoiceRecorderStatus>('idle');
  const [available, setAvailable] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  onTranscriptRef.current = onTranscript;
  onErrorRef.current = onError;

  const supported = isVoiceRecordingSupported();

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    fetchTranscriptionCapability().then((ok) => {
      if (!cancelled) setAvailable(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = async () => {
        releaseStream();
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
        chunksRef.current = [];

        if (blob.size === 0) {
          setStatus('idle');
          return;
        }

        setStatus('transcribing');
        try {
          const form = new FormData();
          form.append('audio', blob, 'voice-input.webm');
          const res = await fetch('/api/transcribe-audio', { method: 'POST', body: form });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}) as { error?: string });
            throw new Error(data.error || `Transcription failed (${res.status})`);
          }
          const data = (await res.json()) as { text?: string };
          const text = (data.text ?? '').trim();
          if (text) {
            onTranscriptRef.current(text);
          } else {
            onErrorRef.current("Didn't catch any speech — try again");
          }
        } catch (error) {
          onErrorRef.current(error instanceof Error ? error.message : 'Transcription failed');
        } finally {
          setStatus('idle');
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setStatus('recording');
    } catch (error) {
      releaseStream();
      setStatus('idle');
      const isPermissionError =
        error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError');
      onErrorRef.current(
        isPermissionError
          ? 'Microphone access denied — allow it in your browser settings'
          : 'Could not access the microphone',
      );
    }
  }, [releaseStream]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
  }, []);

  const toggle = useCallback(() => {
    if (status === 'recording') {
      stopRecording();
    } else if (status === 'idle') {
      void startRecording();
    }
  }, [status, startRecording, stopRecording]);

  // Unmount safety: stop any in-flight recording and release the mic track.
  useEffect(() => {
    return () => {
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop();
      }
      releaseStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { supported, available, status, toggle };
}
